import { useEffect, useRef, useState } from "react";
import { Alert, Box, IconButton, Menu, MenuItem, Tooltip } from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import CircularSpinner from "components/display/circularSpinner";
import { getInternalAppType } from "utils/brandingUtils";
import { useTranslation } from "utils/locales/utilityFunctions";
import type QRCodeStyling from "qr-code-styling";
import { createAudienceQr } from "./createAudienceQr";

export default function AudienceQrCode({ url, title }: { url: string; title: string }) {
  const { translate } = useTranslation();
  const platform = getInternalAppType({ pathOrHost: window.location.host });
  const [qr, setQr] = useState<QRCodeStyling | null>(null);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const preview = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(false);
    setQr(null);
    preview.current?.replaceChildren();
    createAudienceQr(url, platform).then(instance => {
      if (!active) return;
      if (preview.current) instance.append(preview.current);
      setQr(instance);
    }).catch(() => { if (active) setError(true); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [url, platform]);

  const download = async (extension: "png" | "svg") => {
    setAnchor(null);
    if (!qr) return;
    setDownloading(true);
    setError(false);
    try {
      await qr.download({ extension, name: `${title.replace(/[^\p{L}\p{N}_-]+/gu, "-") || "map"}-audience-qr` });
    } catch { setError(true); }
    finally { setDownloading(false); }
  };

  return <Box sx={{ mt: 1 }}>
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, p: 1, borderRadius: 2,
      bgcolor: "secondary.lighter", width: "fit-content", maxWidth: "100%" }}>
      <Box sx={{ position: "relative", width: 160, aspectRatio: "1", maxWidth: "100%", flexShrink: 1 }}>
        <Box ref={preview} role="img" aria-label={`${translate("share-audience-qr")}: ${title}`}
          sx={{ width: "100%", height: "100%", flexShrink: 1,
            display: "flex", alignItems: "center", justifyContent: "center",
            "& svg": { display: "block", width: "100%", height: "auto" } }} />
        {loading && <Box sx={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center" }}><CircularSpinner loading variant="medium" /></Box>}
      </Box>
      <Tooltip title={translate("dot-menu-tooltip")}>
        <Box component="span" sx={{ alignSelf: "flex-end" }}><IconButton aria-label={translate("share-audience-qr-menu")} disabled={!qr || downloading}
          onClick={event => setAnchor(event.currentTarget)}
          sx={{ bgcolor: "background.paper", borderRadius: 1, alignSelf: "flex-end" }}>
          {downloading ? <CircularSpinner loading variant="iconButton" /> : <MoreVertIcon fontSize="small" color="primary" />}
        </IconButton></Box>
      </Tooltip>
    </Box>
    {error && <Alert severity="error" sx={{ mt: 1 }}>{translate("share-audience-qr-error")}</Alert>}
    <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)}>
      <MenuItem onClick={() => download("png")}>{translate("share-audience-qr-download-png")}</MenuItem>
      <MenuItem onClick={() => download("svg")}>{translate("share-audience-qr-download-svg")}</MenuItem>
    </Menu>
  </Box>;
}
